
import {
  formatCount,
  quotaSignal,
  remainingPercent,
  type AccountCapacity,
  type QuotaChannel,
  type QuotaSignal,
} from '@/features/marketing-shared';

export type NoticeKind = 'quota-approaching' | 'quota-exhausted' | 'campaign-cut';

export interface UserNotice {
  id: string;
  kind: NoticeKind;
  channel: QuotaChannel;
  title: string;
  body: string;
  createdAt: string;
  read: boolean;
  href?: string;
}

const CHANNEL_LABEL: Record<QuotaChannel, string> = {
  email: 'e-mail',
  sms: 'SMS',
};

export function noticeId(passbookId: string, channel: QuotaChannel, kind: NoticeKind): string {
  return `${passbookId}:${channel}:${kind}`;
}

export function quotaNotice(
  passbookId: string,
  channel: QuotaChannel,
  signal: Exclude<QuotaSignal, 'ok'>,
  quota: AccountCapacity,
  now = new Date().toISOString(),
): UserNotice {
  const label = CHANNEL_LABEL[channel];
  if (signal === 'exhausted') {
    return {
      id: noticeId(passbookId, channel, 'quota-exhausted'),
      kind: 'quota-exhausted',
      channel,
      title: `Cota de ${label} esgotada`,
      body: `Não há slots de ${label} disponíveis neste passbook. Campanhas desse canal param até uma recarga.`,
      createdAt: now,
      read: false,
    };
  }
  const left = remainingPercent(quota);
  return {
    id: noticeId(passbookId, channel, 'quota-approaching'),
    kind: 'quota-approaching',
    channel,
    title: `Cota de ${label} perto do limite`,
    body: `Restam ${formatCount(quota.available)} de ${formatCount(quota.cycleLimit)} slots (${left}%). Cada envio reduz esse saldo.`,
    createdAt: now,
    read: false,
  };
}

export function campaignCutNotice(input: {
  passbookId: string;
  channel: QuotaChannel;
  campaignName: string;
  sendPeople: number;
  leftOut: number;
  now?: string;
}): UserNotice {
  const label = CHANNEL_LABEL[input.channel];
  return {
    id: noticeId(input.passbookId, input.channel, 'campaign-cut'),
    kind: 'campaign-cut',
    channel: input.channel,
    title: `Campanha de ${label} seria cortada`,
    body: `"${input.campaignName}" cabe para ${formatCount(input.sendPeople)}. ${formatCount(input.leftOut)} ficam de fora se não houver recarga.`,
    createdAt: input.now ?? new Date().toISOString(),
    read: false,
  };
}

export interface NoticeSyncResult {
  next: UserNotice[];
  created: UserNotice[];
}

function upsert(list: UserNotice[], notice: UserNotice): NoticeSyncResult {
  const index = list.findIndex((item) => item.id === notice.id);
  if (index === -1) return { next: [notice, ...list], created: [notice] };
  const current = list[index];
  if (current.body === notice.body && current.title === notice.title) return { next: list, created: [] };
  const next = list.slice();
  next[index] = { ...notice, read: false, createdAt: current.createdAt };
  return { next, created: [next[index]] };
}

function without(list: UserNotice[], id: string): UserNotice[] {
  return list.filter((item) => item.id !== id);
}

/** Atualiza o aviso de limite de um canal. Some quando a cota volta ao normal. */
export function syncQuotaNotice(
  list: UserNotice[],
  passbookId: string,
  channel: QuotaChannel,
  quota: AccountCapacity,
  now?: string,
): NoticeSyncResult {
  const signal = quotaSignal(quota);
  const approachingId = noticeId(passbookId, channel, 'quota-approaching');
  const exhaustedId = noticeId(passbookId, channel, 'quota-exhausted');
  if (signal === 'ok') return { next: without(without(list, approachingId), exhaustedId), created: [] };
  if (signal === 'exhausted') {
    const cleared = without(list, approachingId);
    return upsert(cleared, quotaNotice(passbookId, channel, 'exhausted', quota, now));
  }
  const cleared = without(list, exhaustedId);
  return upsert(cleared, quotaNotice(passbookId, channel, 'approaching', quota, now));
}

export function syncCampaignCut(list: UserNotice[], notice: UserNotice): NoticeSyncResult {
  return upsert(list, notice);
}

/** Tira os avisos de um canal que o passbook não contratou. Devolve a mesma lista se não havia nenhum. */
export function withoutChannelNotices(list: UserNotice[], passbookId: string, channel: QuotaChannel): UserNotice[] {
  const prefix = `${passbookId}:${channel}:`;
  const next = list.filter((item) => !item.id.startsWith(prefix));
  return next.length === list.length ? list : next;
}
